import { useEffect, useState, JSX } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useMySubscription } from "../../hooks/useSubscription";
import styles from "./StripeRedirect.module.css";
import logo from "../../assets/logo.png";

const StripeRedirect = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { execute: fetchSubscription } = useMySubscription();

  const sessionId = searchParams.get("session_id");
  const state = location.state as { planName?: string; price?: number } | null;
  const planName = state?.planName ?? "Pro";
  const price = state?.price ?? 149;

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setStatus("loading");
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;

    const pollSubscription = async () => {
      attempts++;
      const sub = await fetchSubscription();
      if (sub && sub.status === "active") {
        setStatus("success");
        setTimeout(() => navigate("/domain-entry", { replace: true }), 1500);
        return;
      }
      if (attempts >= maxAttempts) {
        setStatus("error");
        setErrorMsg("Payment verification timed out. Please contact support if you were charged.");
        return;
      }
      setTimeout(pollSubscription, 2000);
    };

    pollSubscription();
  }, [sessionId, fetchSubscription, navigate]);

  useEffect(() => {
    if (!sessionId && !state) {
      navigate("/plans", { replace: true });
    }
  }, [sessionId, state, navigate]);

  return (
    <div className={styles.page}>
      <div className={styles.blobBlue} />
      <div className={styles.blobOrange} />
      <div className={styles.container}>
        <div className={styles.logo}><img src={logo} alt="FlyGDS" className={styles.logoImg} /></div>
        <div className={styles.card}>
          <div className={styles.stripeIconWrapper}>
            <div className={styles.stripeRipple} />
            <div className={styles.stripeCircle}>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><path d="M14.5 12.5c0-1.38 1.12-1.88 2.95-1.88 2.63 0 5.95.8 8.58 2.23V7.1C23.4 5.73 20.52 5 17.5 5 11.5 5 7.5 8.1 7.5 13c0 7.75 10.68 6.5 10.68 9.83 0 1.63-1.42 2.15-3.4 2.15-2.93 0-6.68-1.2-9.63-2.83V28c3.28 1.4 6.6 2 9.63 2 6.13 0 10.35-3.03 10.35-8.03-.03-8.37-10.73-7.05-10.73-9.47z" fill="white" /></svg>
            </div>
          </div>
          <div className={styles.textBlock}>
            <h1 className={styles.title}>
              {status === "loading" && "Verifying Payment"}
              {status === "success" && "Payment Confirmed!"}
              {status === "error" && "Verification Issue"}
            </h1>
            <p className={styles.subtitle}>
              {status === "loading" && (<>Confirming your <span className={styles.planHighlight}>{planName} plan</span> payment</>)}
              {status === "success" && "Redirecting to domain setup..."}
              {status === "error" && errorMsg}
            </p>
          </div>
          <div className={styles.summaryBox}>
            <div className={styles.summaryRow}><span className={styles.summaryLabel}>Plan</span><span className={styles.summaryValue}>{planName}</span></div>
            <div className={styles.summaryDivider} />
            <div className={styles.summaryRow}><span className={styles.summaryLabel}>Amount</span><span className={styles.summaryAmount}>${price}<span className={styles.summaryPeriod}>/mo</span></span></div>
            <div className={styles.summaryDivider} />
            <div className={styles.summaryRow}><span className={styles.summaryLabel}>Status</span><span className={styles.summaryValue}>{status === "success" ? "✅ Paid" : status === "error" ? "⚠️ Issue" : "⏳ Verifying"}</span></div>
          </div>
          <div className={styles.loadingSection}>
            <div className={styles.loadingTrack}><div className={styles.loadingBar} /></div>
            <p className={styles.loadingText}>
              {status === "loading" && "Confirming payment with Stripe..."}
              {status === "success" && "Preparing your workspace..."}
              {status === "error" && (<button onClick={() => navigate("/plans", { replace: true })} style={{ background: "none", border: "none", color: "#1D4ED8", cursor: "pointer", textDecoration: "underline", font: "inherit" }}>Return to plans</button>)}
            </p>
          </div>
          <div className={styles.securityRow}>
            <div className={styles.securityBadge}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>256-bit SSL</div>
            <div className={styles.securityBadge}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>PCI Compliant</div>
            <div className={styles.securityBadge}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20,6 9,17 4,12" /></svg>Cancel anytime</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeRedirect;